import {
  ChakraButton,
  ChakraFlex,
  FAIconName,
  Icon,
  Text,
} from '@equip.health/ui';
import { SurveyName } from '~/lib/constants/survey/SurveyName';
import { GetGroupedAssessmentsResponse } from '~/store/services/assessments';
import { SurveyResponseStatus } from '~/lib/constants/survey/SurveyResponseStatus';
import { AssessmentAction } from '~/lib/constants/patients.constants';
import { Dispatch } from 'react';
import {
  onContinueSurvey,
  onRecordNewSurvey,
} from '~/lib/util/surveyActions.util';
import { useComponentPortalContext } from '~/lib/contexts/ComponentPortalContext';
import { Path } from '~/routes/constants';
import { useHistory } from 'react-router-dom';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { CarePlanEvents } from '~/analytics/carePlanEvents';

type Props = {
  patientId: string;
  surveyName: SurveyName;
  groupedAssessments: GetGroupedAssessmentsResponse;
  updateAssessmentState: Dispatch<AssessmentAction>;
};
export const TreatmentOverviewCardDocumentButton = ({
  patientId,
  updateAssessmentState,
  surveyName,
  groupedAssessments,
}: Props) => {
  const { trackEvent } = useAnalytics();
  const history = useHistory();
  const { setComponentVisibility } = useComponentPortalContext();

  const groupedAssessment = groupedAssessments.surveys.find(
    (ga) => ga.name === surveyName,
  );
  if (!groupedAssessment) {
    // This is unexpected behavior from the BE, all survey types should return even if there are 0 assessments
    return <></>;
  }

  const latestAssessment = groupedAssessment.assessments.find((a) =>
    [
      SurveyResponseStatus.DONE,
      SurveyResponseStatus.NEW,
      SurveyResponseStatus.REQUESTED,
      SurveyResponseStatus.IN_PROGRESS,
    ].includes(a.status as SurveyResponseStatus),
  );

  const onClick = () => {
    if (!latestAssessment) {
      if (surveyName === SurveyName.SAFETY_PLAN) {
        trackEvent(
          CarePlanEvents.clickOnStartSafetyPlanButtonInTreatmentOverviewComponent,
        );
      }
      onRecordNewSurvey(surveyName, updateAssessmentState);
      return;
    }

    if (
      [
        SurveyResponseStatus.NEW,
        SurveyResponseStatus.IN_PROGRESS,
        SurveyResponseStatus.REQUESTED,
      ].includes(latestAssessment.status as SurveyResponseStatus)
    ) {
      if (surveyName === SurveyName.SAFETY_PLAN) {
        trackEvent(
          CarePlanEvents.clickOnResumeSafetyPlanButtonInTreatmentOverviewComponent,
        );
      }
      onContinueSurvey(
        latestAssessment.responseExternalId,
        [latestAssessment],
        setComponentVisibility,
      );
      return;
    }

    if (surveyName === SurveyName.SAFETY_PLAN) {
      trackEvent(
        CarePlanEvents.clickOnViewSafetyPlanButtonInTreatmentOverviewComponent,
      );
    }
    const enumKey = Object.entries(SurveyName).find(
      ([, v]) => v === surveyName,
    )?.[0];
    const query = enumKey ? `?surveyCode=${enumKey}` : '';
    history.push(
      `${Path.MAUD_PATIENTS}/${patientId}/assessments${query}`,
    );
  };

  let icon: FAIconName = FAIconName.fileCheckRegular;
  let iconText = '';

  if (!latestAssessment) {
    icon = FAIconName.filePlusRegular;
    iconText = 'Start';
  } else if (
    [
      SurveyResponseStatus.NEW,
      SurveyResponseStatus.IN_PROGRESS,
      SurveyResponseStatus.REQUESTED,
    ].includes(latestAssessment.status as SurveyResponseStatus)
  ) {
    icon = FAIconName.editRegular;
    iconText = 'Resume';
  }

  return (
    <ChakraButton
      alignItems="center"
      backgroundColor="transparent"
      borderColor="neutral.line"
      borderRadius="12px"
      borderStyle={icon === FAIconName.fileCheckRegular ? 'solid' : 'dashed'}
      borderWidth="1px"
      display="flex"
      flexDirection="column"
      height="64px"
      onClick={onClick}
      padding="12px"
      width="150px"
    >
      <ChakraFlex alignItems="center" gap="5px">
        <Icon color="primary.100" name={icon} />
        {iconText !== '' && (
          <Text color="primary.100" variant="captionMedium">
            {iconText}
          </Text>
        )}
      </ChakraFlex>
      <Text color="neutral.secondary" variant="caption">
        {groupedAssessment.displayName}
      </Text>
    </ChakraButton>
  );
};
